import React, { useState, useEffect } from 'react';
import {
  Table, Card, Button, Input, Select, DatePicker, Space, Tag, 
  message, Modal, Tooltip, Row, Col, Statistic, Typography,
  Popconfirm, Badge, Drawer, Form, Radio
} from 'antd';
import {
  BellOutlined, MessageOutlined, WhatsAppOutlined, 
  ReloadOutlined, SendOutlined, DeleteOutlined,
  EyeOutlined, SearchOutlined, FilterOutlined,
  ExclamationCircleOutlined, CheckCircleOutlined,
  ClockCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { notificationAPI, Notification, NotificationStats, NotificationFilters } from '../../services/notificationAPI';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface NotificationsListProps {
  selectedCompany?: any;
}

const NotificationsList: React.FC<NotificationsListProps> = ({ selectedCompany }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState<NotificationStats | null>(null);
  const [filters, setFilters] = useState<NotificationFilters>({});
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [testForm] = Form.useForm();

  useEffect(() => {
    loadNotifications();
    loadStatistics();
  }, [selectedCompany, filters]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.getNotifications(filters);
      
      if (response.success) {
        setNotifications(response.data || []);
      } else {
        message.error(response.message || 'Bildirişlər yüklənmədi');
      }
    } catch (error) {
      console.error('Notifications load error:', error);
      message.error('Bildirişlər yüklənmədi');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await notificationAPI.getNotificationStatistics();
      
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Statistics load error:', error);
    }
  };

  const handleRetry = async (notification: Notification) => {
    try {
      const response = await notificationAPI.retryNotification(notification.id);
      
      if (response.success) {
        message.success('Bildiriş təkrar göndərildi');
        loadNotifications();
      } else {
        message.error(response.message || 'Təkrar göndərmə uğursuz oldu');
      }
    } catch (error) {
      console.error('Retry error:', error);
      message.error('Təkrar göndərmə xətası');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await notificationAPI.deleteNotification(id);
      
      if (response.success) {
        message.success('Bildiriş silindi');
        loadNotifications();
      } else {
        message.error(response.message || 'Bildiriş silinmədi');
      }
    } catch (error) {
      console.error('Delete error:', error);
      message.error('Silmə xətası');
    }
  };

  const handleSendTodayReminders = async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.sendTodayPaymentReminders();
      
      if (response.success) {
        const results = response.results;
        message.success(`${results.sent} SMS göndərildi, ${results.failed} xəta, ${results.no_phone} telefon yoxdur`);
        loadNotifications();
        loadStatistics();
      } else {
        message.error(response.message || 'SMS göndərilmədi');
      }
    } catch (error) {
      console.error('Send today reminders error:', error);
      message.error('SMS göndərmə xətası');
    } finally {
      setLoading(false);
    }
  };
  const handleCreatePaymentReminders = async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.createPaymentReminders();
      
      if (response.success) {
        message.success(response.message || 'Ödəniş xatırlatmaları yaradıldı');
        loadNotifications();
        loadStatistics();
      } else {
        message.error(response.message || 'Xatırlatmalar yaradılmadı');
      }
    } catch (error) {
      console.error('Create reminders error:', error);
      message.error('Xatırlatma yaratma xətası');
    } finally {
      setLoading(false);
    }
  };

  const handleSendPending = async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.sendPendingNotifications();
      
      if (response.success) {
        message.success(response.message || 'Gözləyən bildirişlər göndərildi');
        loadNotifications();
        loadStatistics();
      } else {
        message.error(response.message || 'Bildirişlər göndərilmədi');
      }
    } catch (error) {
      console.error('Send pending error:', error);
      message.error('Göndərmə xətası');
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async (values: any) => {
    try {
      const response = await notificationAPI.sendTestNotification(values);
      
      if (response.success) {
        message.success('Test bildirişi göndərildi');
        setTestModalVisible(false);
        testForm.resetFields();
        loadNotifications();
      } else {
        message.error(response.message || 'Test bildirişi göndərilmədi');
      }
    } catch (error) {
      console.error('Test notification error:', error);
      message.error('Test bildirişi xətası');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case 'sent':
        return <SendOutlined style={{ color: '#1890ff' }} />;
      case 'delivered':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'read':
        return <EyeOutlined style={{ color: '#52c41a' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'cancelled':
        return <ExclamationCircleOutlined style={{ color: '#d9d9d9' }} />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'sent': return 'blue';
      case 'delivered': return 'green';
      case 'read': return 'green';
      case 'failed': return 'red';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'sms':
        return <MessageOutlined style={{ color: '#1890ff' }} />;
      case 'whatsapp':
        return <WhatsAppOutlined style={{ color: '#25d366' }} />;
      default:
        return <BellOutlined />;
    }
  };

  const columns = [
    {
      title: 'Alıcı',
      dataIndex: 'recipient_name',
      key: 'recipient_name',
      render: (text: string, record: Notification) => (
        <div>
          <div className="font-medium">{text}</div>
          <div className="text-sm text-gray-500">{record.recipient_phone}</div>
        </div>
      ),
    },
    {
      title: 'Növ',
      dataIndex: 'notification_type',
      key: 'notification_type',
      width: 100,
      render: (type: string, record: Notification) => (
        <div className="flex items-center space-x-2">
          {getTypeIcon(type)}
          <span>{record.notification_type_display}</span>
        </div>
      ),
    },
    {
      title: 'Mesaj Növü',
      dataIndex: 'message_type_display',
      key: 'message_type_display',
      width: 150,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string, record: Notification) => (
        <div className="flex items-center space-x-2">
          {getStatusIcon(status)}
          <Tag color={getStatusColor(status)}>
            {record.status_display}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Planlaşdırılmış',
      dataIndex: 'scheduled_at',
      key: 'scheduled_at',
      width: 150,
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-',
    },
    {
      title: 'Göndərildi',
      dataIndex: 'sent_at',
      key: 'sent_at',
      width: 150,
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-',
    },
    {
      title: 'SMS Sayı',
      dataIndex: 'sms_count',
      key: 'sms_count',
      width: 80,
      render: (count: number, record: Notification) => 
        record.notification_type === 'sms' ? count : '-',
    },
    {
      title: 'Əməliyyatlar',
      key: 'actions',
      width: 150,
      render: (_: any, record: Notification) => (
        <Space>
          <Tooltip title="Detallar">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedNotification(record);
                setDetailsVisible(true);
              }}
            />
          </Tooltip>
          
          {record.status === 'failed' && (
            <Tooltip title="Təkrar göndər">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => handleRetry(record)}
              />
            </Tooltip>
          )}
          
          {record.status === 'pending' && (
            <Popconfirm
              title="Bu bildirişi silmək istədiyinizə əminsiniz?"
              onConfirm={() => handleDelete(record.id)}
              okText="Bəli"
              cancelText="Xeyr"
            >
              <Tooltip title="Sil">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <Title level={2} className="mb-2">
          <BellOutlined className="mr-2" />
          Bildirişlər
        </Title>
        <Text type="secondary">
          SMS və WhatsApp bildirişlərini izləyin və idarə edin
        </Text>
        
        {/* Bu gün ödənişi olanlar üçün SMS düyməsi */}
        <div className="mt-4">
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSendTodayReminders}
            loading={loading}
            size="large"
          >
            Bu gün ödənişi olanlara SMS göndər
          </Button>
        </div>
      </div>

      {/* Statistikalar */}
      {statistics && (
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Ümumi Bildirişlər"
                value={statistics.total_notifications}
                prefix={<BellOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Son 30 Gün"
                value={statistics.recent_notifications}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Uğur Nisbəti"
                value={statistics.success_rate}
                suffix="%"
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: statistics.success_rate > 80 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Uğursuz"
                value={statistics.by_status.failed || 0}
                prefix={<CloseCircleOutlined />}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filterlər və Əməliyyatlar */}
      <Card className="mb-4">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Axtar..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              allowClear
            />
          </Col>
          
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Növ"
              value={filters.notification_type}
              onChange={(value) => setFilters({ ...filters, notification_type: value })}
              allowClear
              style={{ width: '100%' }}
            >
              <Select.Option value="sms">SMS</Select.Option>
              <Select.Option value="whatsapp">WhatsApp</Select.Option>
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Status"
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              allowClear
              style={{ width: '100%' }}
            >
              <Select.Option value="pending">Gözləyir</Select.Option>
              <Select.Option value="sent">Göndərilib</Select.Option>
              <Select.Option value="delivered">Çatıb</Select.Option>
              <Select.Option value="read">Oxunub</Select.Option>
              <Select.Option value="failed">Uğursuz</Select.Option>
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              value={filters.date_from && filters.date_to ? [
                dayjs(filters.date_from),
                dayjs(filters.date_to)
              ] : undefined}
              onChange={(dates) => {
                if (dates) {
                  setFilters({
                    ...filters,
                    date_from: dates[0]?.format('YYYY-MM-DD'),
                    date_to: dates[1]?.format('YYYY-MM-DD')
                  });
                } else {
                  setFilters({
                    ...filters,
                    date_from: undefined,
                    date_to: undefined
                  });
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
          
          
        </Row>
      </Card>

      {/* Cədvəl */}
      <Card>
        <Table
          columns={columns}
          dataSource={notifications}
          rowKey="id"
          loading={loading}
          pagination={{
            total: notifications.length,
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Ümumi ${total} bildiriş`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Detallar Drawer */}
      <Drawer
        title="Bildiriş Detalları"
        placement="right"
        width={600}
        open={detailsVisible}
        onClose={() => setDetailsVisible(false)}
      >
        {selectedNotification && (
          <div className="space-y-4">
            <div>
              <Text strong>Alıcı:</Text>
              <div>{selectedNotification.recipient_name}</div>
              <div className="text-sm text-gray-500">{selectedNotification.recipient_phone}</div>
            </div>
            
            <div>
              <Text strong>Növ:</Text>
              <div className="flex items-center space-x-2 mt-1">
                {getTypeIcon(selectedNotification.notification_type)}
                <span>{selectedNotification.notification_type_display}</span>
              </div>
            </div>
            
            <div>
              <Text strong>Status:</Text>
              <div className="flex items-center space-x-2 mt-1">
                {getStatusIcon(selectedNotification.status)}
                <Tag color={getStatusColor(selectedNotification.status)}>
                  {selectedNotification.status_display}
                </Tag>
              </div>
            </div>
            
            <div>
              <Text strong>Mesaj Məzmunu:</Text>
              <div className="mt-1 p-3 bg-gray-50 rounded border">
                {selectedNotification.message_content}
              </div>
            </div>
            
            {selectedNotification.notification_type === 'sms' && (
              <div>
                <Text strong>SMS Sayı:</Text>
                <div>{selectedNotification.sms_count}</div>
              </div>
            )}
            
            {selectedNotification.error_message && (
              <div>
                <Text strong className="text-red-600">Xəta Mesajı:</Text>
                <div className="mt-1 p-3 bg-red-50 rounded border border-red-200 text-red-700">
                  {selectedNotification.error_message}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Text strong>Planlaşdırılmış:</Text>
                <div>{selectedNotification.scheduled_at ? 
                  dayjs(selectedNotification.scheduled_at).format('DD.MM.YYYY HH:mm') : '-'}</div>
              </div>
              
              <div>
                <Text strong>Göndərildi:</Text>
                <div>{selectedNotification.sent_at ? 
                  dayjs(selectedNotification.sent_at).format('DD.MM.YYYY HH:mm') : '-'}</div>
              </div>
              
              <div>
                <Text strong>Çatdı:</Text>
                <div>{selectedNotification.delivered_at ? 
                  dayjs(selectedNotification.delivered_at).format('DD.MM.YYYY HH:mm') : '-'}</div>
              </div>
              
              <div>
                <Text strong>Oxundu:</Text>
                <div>{selectedNotification.read_at ? 
                  dayjs(selectedNotification.read_at).format('DD.MM.YYYY HH:mm') : '-'}</div>
              </div>
            </div>
            
            {selectedNotification.status === 'failed' && (
              <div className="pt-4">
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    handleRetry(selectedNotification);
                    setDetailsVisible(false);
                  }}
                  block
                >
                  Təkrar Göndər
                </Button>
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* Test Modal */}
      <Modal
        title="Test Bildirişi Göndər"
        open={testModalVisible}
        onCancel={() => {
          setTestModalVisible(false);
          testForm.resetFields();
        }}
        onOk={() => testForm.submit()}
        okText="Göndər"
        cancelText="Ləğv et"
      >
        <Form
          form={testForm}
          layout="vertical"
          onFinish={handleTestNotification}
        >
          <Form.Item
            label="Telefon Nömrəsi"
            name="phone_number"
            rules={[{ required: true, message: 'Telefon nömrəsi daxil edin' }]}
          >
            <Input placeholder="+994501234567" />
          </Form.Item>
          
          <Form.Item
            label="Bildiriş Növü"
            name="notification_type"
            rules={[{ required: true, message: 'Bildiriş növünü seçin' }]}
            initialValue="sms"
          >
            <Radio.Group>
              <Radio value="sms">SMS</Radio>
              <Radio value="whatsapp">WhatsApp</Radio>
            </Radio.Group>
          </Form.Item>
          
          <Form.Item
            label="Mesaj"
            name="message"
            rules={[{ required: true, message: 'Mesaj daxil edin' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Test mesajınızı yazın..."
              showCount
              maxLength={765}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NotificationsList;